import { v4 as uuidv4 } from 'uuid';

import { type CalendarDate, fromCalendarDate, today } from './dateutils';
import type { DocumentCategoryWithoutReadIdAndClientId } from './documents/folderUtils';
import { MAX_SIZE_ATTACHMENT } from './emailUtils';
import { getDecryptedCopyKey, getPublicFileKey } from './fileKeyUtils';
import { getTypedLanguage } from './i18nutils';
import { isNilOrEmpty } from './objectUtils';
import { type File as FileModel, DocumentType } from './types/model-app';

export enum EntityType {
  ADVERTISEMENT = 'Advertisement',
  BUILDING = 'Building',
  CHARGE = 'Charge',
  CLIENT = 'Client',
  COMMUNICATION = 'Communication',
  CONTACT = 'Contact',
  INTERNAL_COMMENT = 'InternalComment',
  INVOICE = 'Invoice',
  LEASE = 'Lease',
  REPORT = 'Report',
  STATEMENT = 'Statement',
  TECHNIC = 'Technic',
  TICKET = 'Ticket',
  UNIT = 'Unit',
  LEASE_PRICE_HISTORY = 'LeasePriceHistory',
  ACCOUNTING_EXPORT = 'AccountingExport',
  PAYMENT_BATCH = 'PaymentBatch',
}

export enum MimeTypes {
  IMAGE = 'image',
  HEIC_IMAGE = 'image/heic',
  HEIF_IMAGE = 'image/heif',
  AVIF_IMAGE = 'image/avif',
  GIF_IMAGE = 'image/gif',
  JPEG_IMAGE = 'image/jpeg',
  PNG_IMAGE = 'image/png',
  WEBP_IMAGE = 'image/webp',
  ALL_IMAGES = 'image/*',
  VIDEO = 'video',
  ALL_VIDEOS = 'video/*',
  PLAIN_TEXT = 'text/plain',
  JSON = 'application/json',
  EXCEL = 'application/vnd.ms-excel',
  WORD = 'application/msword',
  PDF = 'application/pdf',
  CSV = 'text/csv',
  ZIP = 'application/zip',
  OFFICE_DOCUMENT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  SPREADSHEET_DOCUMENT = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  OUTLOOK = 'application/vnd.ms-outlook',
  XML = 'application/xml',
  TEXT_XML = 'text/xml',
  OCTET_STREAM = 'application/octet-stream',
}

export const DEFAULT_ACCEPTED_MIME_TYPES = [
  MimeTypes.ALL_IMAGES,
  MimeTypes.PLAIN_TEXT,
  MimeTypes.EXCEL,
  MimeTypes.PDF,
  MimeTypes.WORD,
  MimeTypes.CSV,
  MimeTypes.OFFICE_DOCUMENT,
  MimeTypes.SPREADSHEET_DOCUMENT,
];

export interface S3Object extends FileModel {
  fileName: string;
  url: string;
  lastModified?: CalendarDate;
  metadata?: object;
}

export interface NewFile {
  size: number;
  mimeType: string;
  name: string;
  file: File;
  fileKey?: string;
}

export interface EmptyFile {
  id: string;
  file: File;
}
export interface EmptyFileWithUrl extends EmptyFile {
  url: string;
}

export const getEmptyFile = (file: File): EmptyFile => {
  return {
    id: `NEW-${uuidv4()}`,
    file,
  };
};

export const getFileMetaData = (file: S3Object | EmptyFileWithUrl | NewFile | File) => {
  const fileObjectIsAProperty = 'file' in file;
  const fileIsS3Object = 'fileName' in file;

  const fileType = fileObjectIsAProperty ? file.file.type : fileIsS3Object ? (file.mimeType ?? '') : file.type;
  const fileSize = fileObjectIsAProperty ? file.file.size : file.size;
  const fileUrl =
    'url' in file ? file.url : fileObjectIsAProperty ? URL.createObjectURL(file.file) : URL.createObjectURL(file);
  const fileName = fileObjectIsAProperty ? file.file.name : fileIsS3Object ? file.fileName : file.name;
  return { fileType, fileSize, fileUrl, fileName };
};

export const getForeignKeyTableName = (type: DocumentType | EntityType): string => {
  let tableName = '';
  switch (type) {
    case DocumentType.Letter:
      tableName = 'Letter';
      break;
    case DocumentType.LeaseTerminationFromLessor:
    case DocumentType.Lease:
    case EntityType.LEASE:
      tableName = 'Lease';
      break;
    case DocumentType.StatementDetail:
    case DocumentType.StatementReceipt:
    case EntityType.STATEMENT:
      tableName = 'Statement';
      break;
    case DocumentType.Receipt:
    case DocumentType.Invoice:
    case EntityType.INVOICE:
      tableName = 'Invoice';
      break;
    case EntityType.ADVERTISEMENT:
      tableName = 'Advertisement';
      break;
    case EntityType.BUILDING:
      tableName = 'Building';
      break;
    case EntityType.CHARGE:
      tableName = 'Charge';
      break;
    case EntityType.PAYMENT_BATCH:
      tableName = 'PaymentBatch';
      break;
    case EntityType.CLIENT:
      tableName = 'Client';
      break;
    case EntityType.COMMUNICATION:
    case EntityType.REPORT:
    case EntityType.ACCOUNTING_EXPORT:
    case DocumentType.LeaseConsolidatedPaymentRequest:
    case DocumentType.LeasePaymentRequest:
    case DocumentType.LeaseFirstReminder:
    case DocumentType.LeaseSecondReminder:
    case DocumentType.LeaseThirdReminder:
    case DocumentType.LeaseFormalNotice:
      tableName = 'Communication';
      break;
    case EntityType.CONTACT:
      tableName = 'Contact';
      break;
    case EntityType.INTERNAL_COMMENT:
      tableName = 'InternalComment';
      break;
    case EntityType.TECHNIC:
      tableName = 'Technic';
      break;
    case EntityType.TICKET:
      tableName = 'Ticket';
      break;
    case EntityType.UNIT:
      tableName = 'Unit';
      break;
    case EntityType.LEASE_PRICE_HISTORY:
    case DocumentType.LeasePriceHistoryIndexationAccepted:
      tableName = 'LeasePriceHistory';
      break;
  }
  return tableName;
};

export const getS3FolderName = (documentType: DocumentType | EntityType): string => {
  if (documentType === EntityType.REPORT) {
    return 'reportTemp';
  }
  if (documentType === EntityType.ACCOUNTING_EXPORT) {
    return 'accountingExportTemp';
  }
  return getForeignKeyTableName(documentType);
};

const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || '';

export const getContactS3FolderName = (contactType: string, contactId: string) => {
  return `${capitalize(contactType.toLowerCase()).replace('_', '')}/${contactId}`;
};

export const getFileKey = (
  clientId: string,
  documentType: DocumentType | EntityType,
  fileName: string,
  withPublicFolder: boolean = false,
  contactType: string | undefined = undefined,
  contactId: string | undefined = undefined
) => {
  const isTempDocumentType = documentType === EntityType.REPORT || documentType === EntityType.ACCOUNTING_EXPORT;
  let fileKey = '';
  if (!isTempDocumentType) fileKey += `${clientId}/`;
  if (documentType === EntityType.CONTACT) fileKey += getContactS3FolderName(contactType ?? '', contactId ?? '');
  else fileKey += getS3FolderName(documentType);
  if (isTempDocumentType) fileKey += `/${clientId}`;
  fileKey += `/${uuidv4()}_${sanitizeFileName(fileName)}`;

  return withPublicFolder ? getPublicFileKey(fileKey) : fileKey;
};

export const getLetterFileKeyForCommunication = (
  clientId: string,
  documentType: DocumentType,
  communicationId: string,
  fileName: string
) => `${clientId}/${getS3FolderName(documentType)}/${communicationId}_${sanitizeFileName(fileName)}`;

export const getTempFileKeyForConvertion = (clientId: string, fileName: string) => {
  return `convertorTemp/${clientId}/heic/${uuidv4()}_${sanitizeFileName(fileName)}`;
};

export const getTempFileKeyForCommunicationPdf = (clientId: string, communicationId: string, uniqueSuffix?: string) => {
  const fileNameBase = isNilOrEmpty(uniqueSuffix) ? communicationId : `${communicationId}-${uniqueSuffix}`;
  return `convertorTemp/${clientId}/Communication/${fileNameBase}.pdf`;
};

export const sanitizeFileName = (fileName: string) => {
  return fileName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ /g, '_')
    .replace(/[^a-zA-Z0-9_.\-~*'()!%]/g, '');
};

const UP2RENT_FILES_LABEL = 'up2rentFiles';

export const buildDatedFileNameBase = (label: string, date: CalendarDate = today()): string =>
  `${fromCalendarDate(date)}_${sanitizeFileName(label).replace(/_+/g, '_')}`;

export const getUp2RentFilesZipFileName = (): string => `${buildDatedFileNameBase(UP2RENT_FILES_LABEL)}.zip`;

const RFC5987_RESERVED_CHARACTERS = /['()*]/g;

const encodeRFC5987ValueChars = (value: string): string =>
  encodeURIComponent(value).replace(
    RFC5987_RESERVED_CHARACTERS,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
  );

export const getAttachmentContentDisposition = (fileName: string): string => {
  const asciiFallback = sanitizeFileName(fileName);
  return `attachment; filename="${asciiFallback}"; filename*=UTF-8''${encodeRFC5987ValueChars(fileName)}`;
};

export const getFileNameFromKey = (fileKey: string): string => {
  const fileWithPrefix = fileKey.split('/').pop() ?? '';
  const fileName = fileWithPrefix.split('_').slice(1).join('_');
  return fileName || fileWithPrefix;
};

const UPLOAD_KEY_UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

/** Name of a file uploaded through `getFileKey`: the `<uuid>_` prefix goes, an underscore of the name itself stays. */
export const getUploadedFileNameFromKey = (fileKey: string): string =>
  (fileKey.split('/').pop() ?? '').replace(UPLOAD_KEY_UUID_PREFIX, '');

export const getFileNameFromContentDisposition = (contentDisposition: string) => {
  const extendedMatch = /filename\*=(?:[^']*'[^']*')?([^;\n]*)/i.exec(contentDisposition);
  if (extendedMatch != null && extendedMatch[1]) {
    try {
      return decodeURIComponent(extendedMatch[1].trim());
    } catch {
      // malformed RFC 5987 encoding, fall back to the plain filename parameter
    }
  }
  const filenameRegex = /filename[^*;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(contentDisposition);
  if (matches != null && matches[1]) {
    return matches[1].replace(/['"]/g, '');
  }
  return 'NoName';
};

export const getSanitizedFile = (file: File) => {
  const newFileName = sanitizeFileName(file.name);
  const newFile = new File([file], newFileName, {
    type: file.type,
    lastModified: file.lastModified,
  });
  return newFile;
};

export const fileExists = (file: S3Object | NewFile | File, existingFiles: (S3Object | NewFile | File)[]) =>
  existingFiles.some((existingFile) => {
    const { fileSize, fileName } = getFileMetaData(file);
    const { fileSize: existingFileSize, fileName: existingFileName } = getFileMetaData(existingFile);
    return existingFileName === fileName && existingFileSize === fileSize;
  });

export const getTranslatedCategory = (
  category: DocumentCategoryWithoutReadIdAndClientId,
  language: string | null
): string => {
  return category.labels.find((label) => getTypedLanguage(label.language) === getTypedLanguage(language))?.label ?? '';
};

export const addS3Props = (file: FileModel) => {
  const encodedFileName = file.key.substring(file.key.indexOf('_') + 1);
  let fileName = '';
  try {
    fileName = decodeURIComponent(encodedFileName);
  } catch (_error) {
    fileName = encodedFileName;
  }
  return {
    ...file,
    fileName,
    url: '',
  };
};

export interface FileNameValidationResult {
  isValid: boolean;
  message?: string;
}

const RESERVED_FILENAMES = new Set([
  'CON',
  'PRN',
  'AUX',
  'NUL',
  'COM0',
  'COM1',
  'COM2',
  'COM3',
  'COM4',
  'COM5',
  'COM6',
  'COM7',
  'COM8',
  'COM9',
  'COM¹',
  'COM²',
  'COM³',
  'LPT0',
  'LPT1',
  'LPT2',
  'LPT3',
  'LPT4',
  'LPT5',
  'LPT6',
  'LPT7',
  'LPT8',
  'LPT9',
  'LPT¹',
  'LPT²',
  'LPT³',
]);

export const validateBaseName = (baseName: string): FileNameValidationResult => {
  // Check for empty name
  if (!baseName || baseName.trim() === '') {
    return {
      isValid: false,
      message: 'fileNameRequired',
    };
  }

  // Check length (255 is common OS limit)
  if (baseName.length > 255) {
    return {
      isValid: false,
      message: 'fileNameTooLong',
    };
  }

  // Check trailing characters
  if (baseName.endsWith('.')) {
    return {
      isValid: false,
      message: 'fileNameEnding',
    };
  }

  // Check for invalid characters (combined pattern for Windows and macOS)
  const invalidChars = /[<>:"\/\\|?*\x00-\x1F]/;
  if (invalidChars.test(baseName)) {
    return {
      isValid: false,
      message: 'fileNameInvalidChars',
    };
  }

  // Check for reserved Windows names (case-insensitive)
  if (RESERVED_FILENAMES.has(baseName.toUpperCase())) {
    return {
      isValid: false,
      message: `fileNameReserved`,
    };
  }

  // All checks passed
  return { isValid: true };
};

// Mirrors the extensions Amazon SES refuses as attachments: sending one makes SES answer
// BadRequestException, so it is worth refusing before the upload rather than after the send.
const SES_BLOCKED_ATTACHMENT_EXTENSIONS = new Set([
  'ade',
  'adp',
  'app',
  'asp',
  'bas',
  'bat',
  'cer',
  'chm',
  'cmd',
  'com',
  'cpl',
  'crt',
  'csh',
  'der',
  'exe',
  'fxp',
  'gadget',
  'hlp',
  'hta',
  'inf',
  'ins',
  'isp',
  'its',
  'js',
  'jse',
  'ksh',
  'lib',
  'lnk',
  'mad',
  'maf',
  'mag',
  'mam',
  'maq',
  'mar',
  'mas',
  'mat',
  'mau',
  'mav',
  'maw',
  'mda',
  'mdb',
  'mde',
  'mdt',
  'mdw',
  'mdz',
  'msc',
  'msh',
  'msh1',
  'msh1xml',
  'msh2',
  'msh2xml',
  'mshxml',
  'msi',
  'msp',
  'mst',
  'ops',
  'pcd',
  'pif',
  'plg',
  'prf',
  'prg',
  'ps1',
  'ps1xml',
  'ps2',
  'ps2xml',
  'psc1',
  'psc2',
  'reg',
  'scf',
  'scr',
  'sct',
  'shb',
  'shs',
  'sys',
  'tmp',
  'url',
  'vb',
  'vbe',
  'vbs',
  'vps',
  'vsmacros',
  'vss',
  'vst',
  'vsw',
  'vxd',
  'ws',
  'wsc',
  'wsf',
  'wsh',
  'xnk',
]);

export const getBlockedAttachmentExtension = (fileName: string): string | null => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return null;
  }
  const extension = fileName.slice(lastDotIndex + 1).toLowerCase();
  return SES_BLOCKED_ATTACHMENT_EXTENSIONS.has(extension) ? extension : null;
};

export const BYTES_PER_KILOBYTE = 1024;
export const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE;
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];
const MIME_TYPE_SEPARATOR = '/';
const HEIC_MIME_TYPES: string[] = [MimeTypes.HEIC_IMAGE, MimeTypes.HEIF_IMAGE];
const XML_MIME_TYPES: string[] = [MimeTypes.XML, MimeTypes.TEXT_XML];
const TEXT_MIME_TYPES: string[] = [MimeTypes.PLAIN_TEXT, ...XML_MIME_TYPES, MimeTypes.JSON];
const GENERIC_MIME_TYPES: string[] = ['', MimeTypes.OCTET_STREAM];
const XML_EXTENSION = 'XML';
const MIME_TYPE_PARAMETER_SEPARATOR = ';';

export const getAttachmentsTotalSize = (files: File[]): number => files.reduce((acc, file) => acc + file.size, 0);

export const isAttachmentsTotalOverLimit = (files: File[], maxTotalSize = MAX_SIZE_ATTACHMENT): boolean =>
  getAttachmentsTotalSize(files) > maxTotalSize;

export const bytesToMegabytes = (bytes: number): string => (bytes / BYTES_PER_MEGABYTE).toFixed(1);

export const formatFileSize = (bytes: number): string => {
  let value = bytes;
  let unitIndex = 0;
  while (value >= BYTES_PER_KILOBYTE && unitIndex < FILE_SIZE_UNITS.length - 1) {
    value /= BYTES_PER_KILOBYTE;
    unitIndex += 1;
  }
  const rounded = unitIndex === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${FILE_SIZE_UNITS[unitIndex]}`;
};

export const getAttachmentExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop() ?? '').toUpperCase() : '';
};

export const mergeNewAttachments = (currentFiles: File[], droppedFiles: File[]): File[] => [
  ...currentFiles,
  ...droppedFiles.filter((file) => !fileExists(file, currentFiles)),
];

export const removeAttachmentAt = (files: File[], index: number): File[] =>
  files.filter((_, fileIndex) => fileIndex !== index);

const hasMimeTypePrefix = (mimeType: string, type: MimeTypes): boolean =>
  mimeType.startsWith(`${type}${MIME_TYPE_SEPARATOR}`);

export const isImageMimeType = (mimeType: string): boolean => hasMimeTypePrefix(mimeType, MimeTypes.IMAGE);

export const isVideoMimeType = (mimeType: string): boolean => hasMimeTypePrefix(mimeType, MimeTypes.VIDEO);

export const isPdfMimeType = (mimeType: string): boolean => mimeType.includes(MimeTypes.PDF);

export const isHeicMimeType = (mimeType: string): boolean => HEIC_MIME_TYPES.includes(mimeType);

export const isTextMimeType = (mimeType: string): boolean => TEXT_MIME_TYPES.includes(mimeType);

/** An XML file such as a UBL e-invoice: its MIME type, or its extension when the MIME type is generic. */
export const isXmlFile = (mimeType?: string | null, fileName?: string | null): boolean => {
  const essence = (mimeType ?? '').split(MIME_TYPE_PARAMETER_SEPARATOR)[0].trim().toLowerCase();
  return (
    XML_MIME_TYPES.includes(essence) ||
    (GENERIC_MIME_TYPES.includes(essence) && getAttachmentExtension(fileName ?? '') === XML_EXTENSION)
  );
};

export const isPreviewableMimeType = (mimeType?: string | null): boolean =>
  !isNilOrEmpty(mimeType) &&
  (isPdfMimeType(mimeType) || isImageMimeType(mimeType) || isVideoMimeType(mimeType) || isTextMimeType(mimeType));

/** The S3 keys holding a file: its own and, unless it is known not to be a PDF, its decrypted copy (REN-5018). */
export const getStoredObjectKeys = ({ key, mimeType }: Pick<FileModel, 'key' | 'mimeType'>): string[] =>
  isNilOrEmpty(mimeType) || isPdfMimeType(mimeType) ? [key, getDecryptedCopyKey(key)] : [key];
